// src/pages/CategoryPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../services/firebase";
import useCart from "../hooks/useCart";

// 🔹 Métadonnées "visuelles" (image + label) basées sur le slug
// 👉 On ne touche pas à Firestore pour ça, c'est juste décoratif.
const CATEGORY_META = {
  "imprimantes-ticket": {
    label: "Imprimantes à ticket",
    image: "/images/categories/imprimante-ticket.jpg",
    descriptionStatic:
      "Imprimantes thermiques rapides pour vos caisses, restaurants, pharmacies et points de vente.",
  },
  "imprimantes-classiques": {
    label: "Imprimantes classiques",
    image: "/images/categories/imprimante-classique.webp",
    descriptionStatic:
      "Imprimantes A4 pour documents, rapports, factures, usage bureau ou professionnel.",
  },
  "rouleaux-papier": {
    label: "Rouleaux de papier",
    image: "/images/categories/rouleaux-papier.jpg",
    descriptionStatic:
      "Rouleaux thermiques pour imprimantes ticket, TPE, EPOS et terminaux de paiement.",
  },
  tpe: {
    label: "TPE (Terminaux de paiement)",
    image: "/images/categories/tpe.jpg",
    descriptionStatic:
      "Terminaux de paiement pour encaisser par carte bancaire et mobile money en toute simplicité.",
  },
  epos: {
    label: "EPOS / Caisses tactiles",
    image: "/images/categories/epos.jpg",
    descriptionStatic:
      "Systèmes de caisse complets : écran tactile, imprimante, tiroir-caisse et logiciels adaptés.",
  },
  scanners: {
    label: "Scanners codes-barres",
    image: "/images/categories/scanner.jpg",
    descriptionStatic:
      "Scanners 1D/2D pour lecture rapide de codes-barres en caisse ou en gestion de stock.",
  },
  "caisses-manuelles": {
    label: "Caisses manuelles",
    image: "/images/categories/epos.jpeg",
    descriptionStatic:
      "Caisses à tiroir manuelles robustes pour boutiques, pharmacies et points d’encaissement.",
  },
};

// Formateur de prix
const formatPrice = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
  }).format(value);

// Transforme "imprimantes-ticket" → "Imprimantes Ticket"
function humanizeSlug(slug) {
  if (!slug) return "Catégorie";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/* 🧱 Skeleton pour une carte produit dans la grille de catégorie */
function ProductSkeletonCard() {
  return (
    <div className="col">
      <div className="ps-product-card h-100 d-flex flex-column rounded-4 border-0 shadow-sm overflow-hidden bg-white">
        <div className="position-relative">
          <div className="ps-product-image bg-light">
            <span className="placeholder w-100 d-block" style={{ height: 140 }} />
          </div>
        </div>
        <div className="px-2 pb-2 pt-2 d-flex flex-column flex-grow-1">
          <div className="placeholder-glow mb-1">
            <span className="placeholder col-9" />
          </div>
          <div className="placeholder-glow mb-1">
            <span className="placeholder col-5" />
          </div>
          <div className="placeholder-glow mb-2">
            <span className="placeholder col-7" />
          </div>
          <div className="mt-auto">
            <div className="placeholder-glow mb-1">
              <span className="placeholder col-4" />
            </div>
            <div className="placeholder-glow">
              <span className="placeholder col-6" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryPage() {
  const { slug } = useParams(); // ex: "imprimantes-ticket"
  const { addItem } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState("default"); // "default" | "priceAsc" | "priceDesc"

  // Stats calculées à partir des produits
  const [stats, setStats] = useState({
    minPrice: null,
    maxPrice: null,
    brands: [],
  });

  // 🔹 Meta de base (label + image) selon le slug (pas de Firestore)
  const baseMeta = CATEGORY_META[slug] || {};
  const label = baseMeta.label || humanizeSlug(slug);
  const heroImage = baseMeta.image || "/images/categories/default-category.jpg";
  const staticDescription =
    baseMeta.descriptionStatic ||
    "Découvrez les équipements et accessoires disponibles dans cette catégorie.";

  // 🟩 1) Charger TOUS les produits de cette catégorie depuis Firestore
  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    const q = query(
      collection(db, "products_prosoft"),
      where("categorySlug", "==", slug),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setProducts(list);
        setLoading(false);

        // 🔎 Calcul des stats à partir des produits
        if (list.length) {
          const priced = list.filter(
            (p) => typeof p.price === "number" && p.price > 0
          );
          const minPrice =
            priced.length > 0
              ? priced.reduce(
                  (min, p) => (p.price < min ? p.price : min),
                  priced[0].price
                )
              : null;
          const maxPrice =
            priced.length > 0
              ? priced.reduce(
                  (max, p) => (p.price > max ? p.price : max),
                  priced[0].price
                )
              : null;

          const brands = Array.from(
            new Set(list.map((p) => p.brand).filter(Boolean))
          ).slice(0, 5); // limite l’affichage

          setStats({ minPrice, maxPrice, brands });
        } else {
          setStats({ minPrice: null, maxPrice: null, brands: [] });
        }
      },
      (err) => {
        console.error("Erreur Firestore CATEGORY_PAGE :", err);
        setLoading(false);
        setStats({ minPrice: null, maxPrice: null, brands: [] });
      }
    );

    return () => unsub();
  }, [slug]);

  // Tri local des produits
  const sortedProducts = useMemo(() => {
    const copy = [...products];
    if (sortMode === "priceAsc") {
      return copy.sort((a, b) => (a.price || 0) - (b.price || 0));
    }
    if (sortMode === "priceDesc") {
      return copy.sort((a, b) => (b.price || 0) - (a.price || 0));
    }
    return copy;
  }, [products, sortMode]);

  const handleAddToCart = (e, p) => {
    e.preventDefault(); // évite de suivre le lien quand on clique sur le bouton
    addItem(
      {
        id: p.id,
        slug: p.slug || p.id,
        name: p.name,
        brand: p.brand || "",
        price: Number(p.price) || 0,
        mainImage:
          p.mainImage ||
          (Array.isArray(p.images) && p.images[0]) ||
          "/images/placeholder-product.jpg",
      },
      1
    );
  };

  // 📝 Description dynamique basée sur la BDD
  const dynamicDescription = useMemo(() => {
    if (!products.length) {
      return staticDescription;
    }

    const parts = [];

    parts.push(
      `Découvrez nos ${products.length} ${
        products.length > 1 ? "articles" : "article"
      } dans la catégorie ${label}.`
    );

    if (stats.minPrice) {
      parts.push(
        `Prix à partir de ${formatPrice(stats.minPrice)} CFA${
          stats.maxPrice && stats.maxPrice !== stats.minPrice
            ? ` jusqu'à ${formatPrice(stats.maxPrice)} CFA`
            : ""
        }.`
      );
    }

    if (stats.brands.length) {
      parts.push(`Principales marques : ${stats.brands.join(" • ")}.`);
    }

    return parts.join(" ");
  }, [
    products.length,
    stats.minPrice,
    stats.maxPrice,
    stats.brands,
    label,
    staticDescription,
  ]);

  return (
    <div className="category-page bg-white py-4">
      <div className="container">
        {/* Fil d’Ariane */}
        <nav className="mb-3 small text-muted">
          <a href="/">Accueil</a> /{" "}
          <a href="/#categories">Matériels &amp; équipements</a> /{" "}
          <span>{label}</span>
        </nav>

        {/* HERO CATÉGORIE : bloc plus “premium” */}
        <div className="category-hero mb-4">
          <div className="row g-3 align-items-stretch">
            <div className="col-md-7 col-lg-8">
              <div className="h-100 d-flex flex-column justify-content-center rounded-4 p-3 p-md-4 border bg-white shadow-sm">
                <h1 className="h4 fw-bold mb-2 text-uppercase">{label}</h1>

                <p className="small text-muted mb-2">{dynamicDescription}</p>

                <p className="small text-muted mb-3">
                  <strong>{products.length}</strong>{" "}
                  {products.length <= 1
                    ? "produit disponible"
                    : "produits disponibles"}{" "}
                  dans cette catégorie.
                </p>

                <div className="d-flex flex-wrap gap-2 small">
                  <span className="badge rounded-pill bg-light text-dark">
                    Vente en boutique &amp; en ligne
                  </span>
                  <span className="badge rounded-pill bg-light text-dark">
                    Assistance WhatsApp avant achat
                  </span>
                  <span className="badge rounded-pill bg-light text-dark">
                    Solutions adaptées aux pros
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BARRE D’ACTIONS (tri) */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
          <div className="small text-muted">
            {loading
              ? "Chargement des produits…"
              : products.length === 0
              ? "Aucun produit pour le moment dans cette catégorie."
              : `Affichage de ${products.length} produit(s).`}
          </div>

          <div className="d-flex align-items-center gap-2 small">
            <span className="text-muted">Trier par :</span>
            <select
              className="form-select form-select-sm w-auto"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              disabled={loading || !products.length}
            >
              <option value="default">Pertinence</option>
              <option value="priceAsc">Prix croissant</option>
              <option value="priceDesc">Prix décroissant</option>
            </select>
          </div>
        </div>

        {/* GRILLE PRODUITS responsive, cartes plus élégantes */}
        <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
          {loading &&
            Array.from({ length: 8 }).map((_, idx) => (
              <ProductSkeletonCard key={idx} />
            ))}

          {!loading && sortedProducts.length === 0 && (
            <div className="col-12">
              <div className="alert alert-light small mb-0">
                Aucun article n&apos;est encore disponible pour cette catégorie.
                N&apos;hésitez pas à nous contacter pour plus d&apos;informations.
              </div>
            </div>
          )}

          {!loading &&
            sortedProducts.map((p) => {
              const imageSrc =
                p.mainImage ||
                (Array.isArray(p.images) && p.images[0]) ||
                "/images/placeholder-product.jpg";

              const priceLabel =
                p.price != null ? `${formatPrice(p.price)}` : null;

              const showPromo = p.oldPrice && p.price != null;

              return (
                <div key={p.id} className="col">
                  <a
                    href={`/produit/${p.slug || p.id}`}
                    className="text-decoration-none text-dark h-100 d-block"
                  >
                    <div className="ps-product-card h-100 d-flex flex-column rounded-4 border-0 shadow-sm overflow-hidden bg-white">
                      {/* Image + badges */}
                      <div className="position-relative">
                        <div className="ps-product-image">
                          <img src={imageSrc} alt={p.name} />
                        </div>

                        {/* Badge promo ou nouveauté */}
                        {showPromo && (
                          <span className="badge bg-danger position-absolute top-0 start-0 m-2">
                            Promo
                          </span>
                        )}
                        {p.isFeatured && (
                          <span className="badge bg-warning text-dark position-absolute top-0 end-0 m-2">
                            Meilleure offre
                          </span>
                        )}
                      </div>

                      {/* Corps de la carte */}
                      <div className="px-2 pb-2 pt-2 d-flex flex-column flex-grow-1">
                        {/* Titre produit */}
                        <div className="ps-product-name small mb-1">
                          {p.name}
                        </div>

                        {/* Marque */}
                        {p.brand && (
                          <div className="small mb-1">
                            <span className="badge bg-light text-dark rounded-pill">
                              {p.brand}
                            </span>
                          </div>
                        )}

                        {/* Description courte */}
                        {p.shortDescription && (
                          <div className="small text-muted mb-2 line-clamp-2">
                            {p.shortDescription}
                          </div>
                        )}

                        {/* Prix + ancien prix plus mis en avant */}
                        <div className="mt-auto">
                          <div className="d-flex flex-column align-items-start mb-2">
                            {priceLabel ? (
                              <div className="d-flex align-items-baseline gap-1">
                                <span className="fw-bold fs-6 text-primary">
                                  {priceLabel}
                                </span>
                                <span className="small text-muted">CFA</span>
                              </div>
                            ) : (
                              <span className="small fw-semibold text-secondary">
                                Prix sur demande
                              </span>
                            )}

                            {p.oldPrice && (
                              <div className="small text-muted text-decoration-line-through">
                                {formatPrice(p.oldPrice)} CFA
                              </div>
                            )}
                          </div>

                          {/* Statut + bouton panier */}
                          <div className="d-flex flex-wrap gap-1 small align-items-center">
                            {p.inStock ? (
                              <span className="badge bg-success-subtle text-success border border-success-subtle">
                                En stock
                              </span>
                            ) : (
                              <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                                Rupture
                              </span>
                            )}

                            <button
                              type="button"
                              className="btn btn-sm btn-primary ms-auto px-3"
                              onClick={(e) => handleAddToCart(e, p)}
                            >
                              + Ajouter
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}


export default CategoryPage;

